import axiosInstance from "../../../config/axios";
import { store } from "../../store";

export interface RetailerPreBook {
  id: number;
  title: string;
  description: string | null;
  startDate: string; // YYYY-MM-DD
  endDate: string;
  showPrice: boolean;
  productCount: number;
}

// Pre-book header returned by the inventory / cart endpoints
export interface PreBookHeader {
  id: number;
  title: string;
  description?: string | null;
  startDate: string;
  endDate: string;
  showPrice: boolean;
}

export type PreBookOrderStatus = "PENDING" | "PROCESSING" | "APPROVED" | "REJECTED";

// One line of a pre-book order request
export interface PreBookOrderLine {
  id: number;
  lineNumber: number;
  itemNumber: number;
  itemDescription: string | null;
  uom: string | null;
  pack: number | null;
  caseCount: number | null;
  qty: number;
  price: number;
  priceWithTax: number;
  totalPrice: number;
  totalPriceWithTax: number;
}

// Order placed from a pre-book: a request (PBO-xxxxx) until the distributor approves it.
// orderNumber is the real order number, set on approve. approvalError / actionBy are admin-only.
export interface PreBookOrder {
  id: number;
  requestNumber: string;
  preBookId: number;
  customerNumber: number;
  customerName: string | null;
  status: PreBookOrderStatus;
  orderNumber: number | null;
  placedBy: "retailer" | "sales";
  isWeb: boolean;
  itemCount: number;
  totalQty: number;
  subTotal: number;
  deliveryCharge: number;
  totalAmount: number;
  shippingDetails?: { method?: string; instructions?: string; selectedTimeSlot?: string; shippingAddress?: string } | null;
  rejectReason?: string | null;
  approvalError?: string | null;
  actionBy?: string | null;
  actionAt?: string | null;
  createdAt: string;
  details?: PreBookOrderLine[];
  preBook?: PreBookHeader & { status?: string };
}

// Is at least one Active, in-date pre-book open to this customer? — response: { data: { available: boolean } }
export const getPreBookAvailability = async () => {
  const response = await axiosInstance.get("/retailer/pre-book/available");
  return response.data;
};

// Should a delivery charge apply to pre-book orders? -- response: { data: { allowed: boolean } }
// true  -> fetch the normal delivery charge and apply it
// false -> delivery charge is 0
export const getPreBookDeliveryChargeAllowed = async () => {
  const response = await axiosInstance.get("/retailer/pre-book/delivery-charge-allowed");
  return response.data;
};

// Pre-books open to this customer right now — response: { data: RetailerPreBook[] }
export const getRetailerPreBooks = async () => {
  const response = await axiosInstance.get("/retailer/pre-book/list");
  return response.data;
};

// Products of one pre-book. Same body/response as /retailer/getInventory, plus `preBook`.
// response: { data: { preBook, totalCount, page, limit, totalPages, finalProductList } }
// The body is built exactly like getInventoryItems (state / zip / jurisdiction from the store)
// so the pre-book product list is priced the same way as the normal order screen.
export const getRetailerPreBookProducts = async (
  preBookId: number | string,
  params: {
    page: number;
    limit: number;
    search?: string;
    masterSearch?: string;
    salesCategoryId?: (number | string)[];
    priceClassId?: (number | string)[];
    shortBy?: number;
    [key: string]: any;
  },
  signal?: AbortSignal
) => {
  const storeDetail = store.getState().auth.storeDetail;
  const payload = {
    ...params,
    state: storeDetail?.C_State,
    zip: storeDetail?.C_Zip,
    jurisdiction: storeDetail?.Jurisdiction_State,
  };
  const response = await axiosInstance.post(`/retailer/pre-book/${preBookId}/inventory`, payload, { signal } as any);
  return response.data;
};

// ───────────────────────── Pre-book cart (R4 – R9) ─────────────────────────
// Each pre-book has its own cart, separate from the normal order cart. Every
// call carries the preBookId. The cart line id for update / remove is
// finalCartItems[i].Product.id — the same shape as /retailer/cart/items.

// R4 — body = the normal /retailer/cart/add body + preBookId. Response 201, data = saved cart line.
export const addToPreBookCart = async (preBookId: number | string, params: any) => {
  const response = await axiosInstance.post("/retailer/pre-book/cart/add", { ...params, preBookId: Number(preBookId) });
  return response.data;
};

// R5 — cart of one pre-book. Response = /retailer/cart/items response + preBook, and each line has `inPreBook`.
export const getPreBookCartItems = async (preBookId: number | string) => {
  const response = await axiosInstance.get("/retailer/pre-book/cart/items", { params: { preBookId: Number(preBookId) } });
  return response.data;
};

// R6 — body = the normal /retailer/cart/items/:id body
export const updatePreBookCartItem = async (cartItemId: any, params: any) => {
  const response = await axiosInstance.put(`/retailer/pre-book/cart/items/${cartItemId}`, params);
  return response.data;
};

// R7
export const removeFromPreBookCart = async (cartItemId: any) => {
  const response = await axiosInstance.delete(`/retailer/pre-book/cart/items/${cartItemId}`);
  return response.data;
};

// R8 — empties that pre-book's cart only (the normal cart is never touched)
export const clearPreBookCart = async (preBookId: number | string) => {
  const response = await axiosInstance.delete("/retailer/pre-book/cart/clear", { params: { preBookId: Number(preBookId) } });
  return response.data;
};

// R9 — remove several lines at once. data: { removed: n }
export const removeMultiplePreBookCartItems = async (cartItemIds: number[]) => {
  const response = await axiosInstance.post("/retailer/pre-book/cart/removeMultipleItems", { cartItemIds });
  return response.data;
};

// R10 — body = the normal /retailer/placeOrder body + preBookId. Response 201,
// data: { requestNumber: "PBO-00012", status: "PENDING", preBookOrder }.
// The order goes to the distributor as a request; the real order is placed when they approve it.
// On success the pre-book cart is closed automatically by the API.
export const placePreBookOrder = async (preBookId: number | string, params: any) => {
  const response = await axiosInstance.post(
    "/retailer/pre-book/placeOrder",
    { ...params, preBookId: Number(preBookId) },
    { headers: { "Content-Type": "application/json", "is-web-order": "true" } }
  );
  return response.data;
};

// R11 — this customer's order requests on one pre-book ("Your orders" tab) → { data: PreBookOrder[] }
export const getPreBookOrders = async (preBookId: number | string) => {
  const response = await axiosInstance.get(`/retailer/pre-book/${preBookId}/orders`);
  return response.data;
};

// R12 — one order request with its lines → { data: PreBookOrder & { details } }
export const getPreBookOrderById = async (orderId: number | string) => {
  const response = await axiosInstance.get(`/retailer/pre-book-orders/${orderId}`);
  return response.data;
};
